/**
 * screensaverSettings — After Dark (decoded / "ripped") screensaver
 * selection for the desktop Screen Saver and Control Panel → Display.
 *
 * Persistence is opt-in. Runtime settings always live in this module;
 * localStorage is only written when the user ticks "Save settings to
 * this computer" in Display Properties. Exit still sweeps the key via
 * winbit32Storage.
 *
 * Artwork and modules come from Vortitron/after-dark (GitHub Pages).
 * CSS-only recreations are intentionally omitted.
 */

export const AFTER_DARK_BASE_URL = 'https://vortitron.github.io/after-dark';

export const SCREENSAVER_STORAGE_KEY = 'wb32:screensaver-settings';

/**
 * Decoded After Dark modules only (`kind: 'ripped'` on the upstream
 * Display Properties page). Options match each module's TYPE_1000
 * control-panel strings.
 */
export const AFTER_DARK_SAVERS = Object.freeze([
	Object.freeze({
		id: 'toasters',
		name: 'Flying Toasters',
		file: 'all/toasters.html',
		tag: 'TOAST3.AD 1992',
		defaults: Object.freeze({ objects: 'air wing' }),
		options: Object.freeze([
			Object.freeze({
				name: 'objects',
				label: 'Objects',
				values: Object.freeze(['air wing', 'squadron', 'swarm']),
			}),
		]),
	}),
	Object.freeze({
		id: 'marbles',
		name: 'Marbles',
		file: 'all/marbles.html',
		tag: 'MARBLES2.AD 1992',
		defaults: Object.freeze({ pins: 'many', 'pin-size': 'medium', speed: 'medium' }),
		options: Object.freeze([
			Object.freeze({
				name: 'pins',
				label: 'Pins',
				values: Object.freeze(['many', 'none', 'few', 'lots']),
			}),
			Object.freeze({
				name: 'pin-size',
				label: 'Pin Size',
				values: Object.freeze(['medium', 'x-small', 'big']),
			}),
			Object.freeze({
				name: 'speed',
				label: 'Speed',
				values: Object.freeze(['medium', 'slow', 'fast']),
			}),
		]),
	}),
	Object.freeze({
		id: 'marbles-tilt',
		name: 'Marbles (Tilt)',
		file: 'all/marbles-tilt.html',
		tag: 'MARBLES2 + accel',
		allow: 'accelerometer; gyroscope',
		// The only module that wants the pointer and the keys for itself: the
		// arrows swing its cow and dragging her steers gravity. The host must
		// therefore leave both alone and wait to be told when to stop — see
		// `after-dark:stop` in AfterDarkScreensaver.
		interactive: true,
		defaults: Object.freeze({ pins: 'many', 'pin-size': 'medium', speed: 'medium' }),
		options: Object.freeze([
			Object.freeze({
				name: 'pins',
				label: 'Pins',
				values: Object.freeze(['many', 'none', 'few', 'lots']),
			}),
			Object.freeze({
				name: 'pin-size',
				label: 'Pin Size',
				values: Object.freeze(['medium', 'x-small', 'big']),
			}),
			Object.freeze({
				name: 'speed',
				label: 'Speed',
				values: Object.freeze(['medium', 'slow', 'fast']),
			}),
		]),
	}),
	Object.freeze({
		id: 'flocks',
		name: 'Flocks',
		file: 'all/flocks.html',
		tag: 'FLOCKS.AD 1992',
		defaults: Object.freeze({ kind: 'birds', size: 'medium' }),
		options: Object.freeze([
			Object.freeze({
				name: 'kind',
				label: 'Flock',
				values: Object.freeze([
					'birds', 'polliwogs', 'gnats', 'paparazzi', 'atoms', 'copters', 'dots',
				]),
			}),
			Object.freeze({
				name: 'size',
				label: 'Size',
				values: Object.freeze(['medium', 'small', 'large']),
			}),
		]),
	}),
	Object.freeze({
		id: 'aqua',
		name: 'Aquatic Realm',
		file: 'all/aqua.html',
		tag: 'Aqua.ad',
		// aqua.html sets neither a `sea-floor` attribute nor an `on` hint
		// upstream, so the module's own answer is "off".
		defaults: Object.freeze({ creatures: '14', seaweed: '6', 'sea-floor': false }),
		options: Object.freeze([
			Object.freeze({
				name: 'creatures',
				label: 'Creatures',
				values: Object.freeze(['14', '4', '8', '19']),
			}),
			Object.freeze({
				name: 'seaweed',
				label: 'Seaweed',
				values: Object.freeze(['6', '0', '3', '10']),
			}),
			Object.freeze({ name: 'sea-floor', label: 'Show Sea Floor', check: true }),
		]),
	}),
	Object.freeze({
		id: 'fishpro',
		name: 'Fish Pro',
		file: 'all/fishpro.html',
		tag: 'FISHPRO.AD 1994',
		defaults: Object.freeze({ fish: 'school', 'sea-floor': 'animated' }),
		options: Object.freeze([
			Object.freeze({
				name: 'fish',
				label: 'Fish',
				values: Object.freeze(['school', 'solo', 'study group', 'class', 'university']),
			}),
			Object.freeze({
				name: 'sea-floor',
				label: 'Show Sea Floor',
				values: Object.freeze(['animated', 'none', 'static']),
			}),
		]),
	}),
	Object.freeze({
		id: 'bugs',
		name: 'Bugs',
		file: 'all/bugs.html',
		tag: 'BUGS.AD 1994',
		defaults: Object.freeze({ density: 'infestation', type: 'all', 'clear-screen': true }),
		options: Object.freeze([
			Object.freeze({
				name: 'density',
				label: 'Bug Density',
				values: Object.freeze([
					'infestation', 'nest', 'colony', 'swarm', 'plague', 'new york',
				]),
			}),
			Object.freeze({
				name: 'type',
				label: 'Bug Type',
				values: Object.freeze([
					'all', 'scarab', 'jewel', 'roaches', 'ants', 'ladybugs', 'flys',
				]),
			}),
			Object.freeze({ name: 'clear-screen', label: 'Clear Screen First', check: true }),
		]),
	}),
	Object.freeze({
		id: 'bungee',
		name: 'Bungee Roulette',
		file: 'all/bungee.html',
		tag: 'BUNGEE.AD 1995',
		defaults: Object.freeze({ jumper: 'random', jumps: 'many', equipment: 'so - so' }),
		options: Object.freeze([
			Object.freeze({
				name: 'jumper',
				label: 'Jumper',
				values: Object.freeze(['random', 'daredevil', 'cow', 'chicken', 'fish']),
			}),
			Object.freeze({
				name: 'jumps',
				label: 'Jumps',
				values: Object.freeze([
					'many', 'one', 'few', 'droves', 'whole bunches', 'hundreds',
				]),
			}),
			Object.freeze({
				name: 'equipment',
				label: 'Equipment',
				values: Object.freeze(['so - so', 'safe', 'reliable', 'used', 'purfikt']),
			}),
		]),
	}),
	Object.freeze({
		id: 'cham',
		name: 'Chameleon',
		file: 'all/cham.html',
		tag: 'CHAM.AD 1995',
		defaults: Object.freeze({ quantity: 'some', zest: 'perky', vomiter: 'sick' }),
		options: Object.freeze([
			Object.freeze({
				name: 'quantity',
				label: 'Quantity',
				values: Object.freeze(['some', 'few', 'more', 'some more', 'lots']),
			}),
			Object.freeze({
				name: 'zest',
				label: 'Zest',
				values: Object.freeze(['perky', 'laconic', 'frisky', 'hyper']),
			}),
			Object.freeze({
				name: 'vomiter',
				label: 'Vomiter',
				values: Object.freeze([
					'sick', 'none', 'upset tummy', 'nauseous', 'poisoned', 'vomitorium',
				]),
			}),
		]),
	}),
	Object.freeze({
		id: 'toxic',
		name: 'Toxic Swamp',
		file: 'all/toxic.html',
		tag: 'Toxic.ad 1995',
		defaults: Object.freeze({
			critters: 'fruitful',
			'lung-capacity': 'quart',
			'fish-only': false,
			'swamp-floor': true,
		}),
		options: Object.freeze([
			Object.freeze({
				name: 'critters',
				label: 'Critters',
				values: Object.freeze([
					'fruitful', 'depopulated', 'lonely', 'swarming', 'festering',
				]),
			}),
			Object.freeze({
				name: 'lung-capacity',
				label: 'Lung Capacity',
				values: Object.freeze(['quart', 'thimble', 'cup', 'gallon', 'barrel']),
			}),
			Object.freeze({ name: 'fish-only', label: 'Fish Only', check: true }),
			Object.freeze({ name: 'swamp-floor', label: 'Show Swamp Floor', check: true }),
		]),
	}),
	Object.freeze({
		id: 'phlegm',
		name: 'Phlegm Boy',
		file: 'all/phlegm.html',
		tag: 'PHLEGM_B.AD 1995',
		defaults: Object.freeze({
			behavior: 'ill-mannered',
			mess: 'nauseous',
			'clear-screen': true,
		}),
		options: Object.freeze([
			Object.freeze({
				name: 'behavior',
				label: 'Behavior',
				values: Object.freeze([
					'ill-mannered', 'pesky', 'unruly', 'horrid', 'atrocious', 'kill it!!!',
				]),
			}),
			Object.freeze({
				name: 'mess',
				label: 'Mess',
				values: Object.freeze([
					'nauseous', 'unkempt', 'besmirched', 'disquieting', 'disgusting',
					'art form', 'are you crazy?!',
				]),
			}),
			Object.freeze({ name: 'clear-screen', label: 'Clear Screen First', check: true }),
		]),
	}),
	Object.freeze({
		id: 'marbles-40',
		name: 'Marbles!',
		file: 'all/marbles-40.html',
		tag: 'MARBLES.AD 4.0',
		defaults: Object.freeze({ count: 'a jar full', pattern: '' }),
		options: Object.freeze([
			Object.freeze({
				name: 'count',
				label: 'Marbles',
				values: Object.freeze(['a jar full', 'a few', 'a pouch full', 'a box full']),
			}),
			Object.freeze({
				name: 'pattern',
				label: 'Peg Pattern',
				values: Object.freeze(['', '1', '2', '3', '4', '5', '6', '7', '8']),
				blankLabel: 'Random',
			}),
		]),
	}),
	Object.freeze({
		id: 'guernsey',
		name: 'Guernsey Madness',
		file: 'all/guernsey.html',
		tag: 'GUERNSEY.AD 4.0',
		defaults: Object.freeze({ flavor: 'tutti-frutti' }),
		options: Object.freeze([
			Object.freeze({
				name: 'flavor',
				label: 'Flavor',
				values: Object.freeze([
					'tutti-frutti', 'vanilla', 'chocolate', 'strawberry', 'blueberry',
					'grape', 'banana', 'lemon-lime', 'rainbow splash', 'bubble gum',
					'cinnamon', 'mint',
				]),
			}),
		]),
	}),
]);

const DEFAULT_SAVER_ID = 'toasters';

/**
 * Idle minutes before the saver takes over, as Win 3.1's Desktop applet put it.
 * 0 is its "off" — the saver then only runs from the window menu or Test.
 */
export const DELAY_CHOICES = Object.freeze([0, 1, 2, 3, 5, 10, 15, 30]);

/**
 * Off unless asked for. A saver that starts itself would have an untouched tab
 * fetching from vortitron.github.io on a timer nobody set, which is not a
 * thing a wallet should do behind the user's back.
 */
const DEFAULT_DELAY_MINUTES = 0;

/** @typedef {{ saverId: string, options: Record<string, string|boolean>, delayMinutes: number, persist: boolean }} ScreensaverSettings */

/** @type {ScreensaverSettings} */
let runtimeSettings = createDefaultSettings();

/** @type {Set<(s: ScreensaverSettings) => void>} */
const listeners = new Set();

function createDefaultSettings() {
	const saver = getSaverById(DEFAULT_SAVER_ID);
	return {
		saverId: DEFAULT_SAVER_ID,
		options: { ...(saver?.defaults || {}) },
		delayMinutes: DEFAULT_DELAY_MINUTES,
		persist: false,
	};
}

/** Coerce anything to one of DELAY_CHOICES, falling back to the default. */
function normaliseDelay(value) {
	const n = Number(value);
	if (!Number.isFinite(n)) return DEFAULT_DELAY_MINUTES;
	return DELAY_CHOICES.includes(n) ? n : DEFAULT_DELAY_MINUTES;
}

export function getSaverById(id) {
	return AFTER_DARK_SAVERS.find((s) => s.id === id) || null;
}

/**
 * Build the iframe / preview URL for a saver + option map.
 *
 * After Dark's `ad.js` reads each setting from the query string first and only
 * falls back to the attribute baked into the module's own page, so every option
 * is sent explicitly rather than diffed against `defaults`. Diffing looks
 * tidier but silently breaks whenever our idea of a module's default drifts
 * from the page's: omit `sea-floor` because we think it is already on, and the
 * page — which has no such attribute — renders it off while the tick stays.
 * Sending everything makes the URL alone decide what runs.
 *
 * Checkboxes use the `yes`/`no` spelling `flag()` understands. A blank value on
 * a list option means "unset" (Marbles!'s Random peg pattern) and is omitted so
 * the module picks for itself.
 */
export function buildScreensaverUrl(saverId, options = {}) {
	const saver = getSaverById(saverId) || getSaverById(DEFAULT_SAVER_ID);
	assert(saver, 'No After Dark saver available');
	const params = new URLSearchParams();
	const merged = { ...saver.defaults, ...options };
	for (const opt of saver.options) {
		const raw = merged[opt.name];
		if (opt.check) {
			params.set(opt.name, raw ? 'yes' : 'no');
			continue;
		}
		const value = raw === undefined || raw === null ? '' : String(raw);
		if (value !== '') params.set(opt.name, value);
	}
	const qs = params.toString();
	return `${AFTER_DARK_BASE_URL}/${saver.file}${qs ? `?${qs}` : ''}`;
}

export function getScreensaverSettings() {
	return {
		saverId: runtimeSettings.saverId,
		options: { ...runtimeSettings.options },
		delayMinutes: runtimeSettings.delayMinutes,
		persist: runtimeSettings.persist,
	};
}

/** Idle milliseconds before the saver starts, or 0 when the timer is off. */
export function getScreensaverIdleMs() {
	return runtimeSettings.delayMinutes * 60_000;
}

export function getActiveScreensaverUrl() {
	return buildScreensaverUrl(runtimeSettings.saverId, runtimeSettings.options);
}

export function getActiveSaverMeta() {
	return getSaverById(runtimeSettings.saverId) || getSaverById(DEFAULT_SAVER_ID);
}

function notify() {
	const snapshot = getScreensaverSettings();
	for (const fn of listeners) {
		try { fn(snapshot); } catch { /* listener noop */ }
	}
}

/**
 * Apply settings for the session. When `persist` is true, also write
 * localStorage; when false, remove any previously saved copy.
 */
export function applyScreensaverSettings({ saverId, options, delayMinutes, persist }) {
	assert(typeof saverId === 'string' && saverId.length > 0, 'saverId required');
	const saver = getSaverById(saverId);
	assert(saver, `Unknown After Dark saver: ${saverId}`);
	const nextOptions = { ...saver.defaults, ...(options || {}) };
	runtimeSettings = {
		saverId,
		options: nextOptions,
		delayMinutes: delayMinutes === undefined
			? runtimeSettings.delayMinutes
			: normaliseDelay(delayMinutes),
		persist: Boolean(persist),
	};
	if (runtimeSettings.persist) {
		writePersistedSettings(runtimeSettings);
	} else {
		clearPersistedSettings();
	}
	notify();
	return getScreensaverSettings();
}

function writePersistedSettings(settings) {
	if (typeof localStorage === 'undefined') return;
	try {
		localStorage.setItem(SCREENSAVER_STORAGE_KEY, JSON.stringify({
			saverId: settings.saverId,
			options: settings.options,
			delayMinutes: settings.delayMinutes,
			persist: true,
			v: 2,
		}));
	} catch { /* quota / private mode */ }
}

export function clearPersistedSettings() {
	if (typeof localStorage === 'undefined') return;
	try { localStorage.removeItem(SCREENSAVER_STORAGE_KEY); } catch { /* noop */ }
}

/**
 * Load optional persisted settings into the runtime. Safe to call on boot.
 * Returns whether a persisted payload was found and applied.
 */
export function hydrateScreensaverSettingsFromStorage() {
	if (typeof localStorage === 'undefined') return false;
	let raw;
	try { raw = localStorage.getItem(SCREENSAVER_STORAGE_KEY); } catch { return false; }
	if (!raw) return false;
	try {
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object') return false;
		if (!getSaverById(parsed.saverId)) return false;
		runtimeSettings = {
			saverId: parsed.saverId,
			options: {
				...(getSaverById(parsed.saverId).defaults),
				...(parsed.options && typeof parsed.options === 'object' ? parsed.options : {}),
			},
			// v1 payloads predate the idle timer and have no delay in them.
			delayMinutes: normaliseDelay(parsed.delayMinutes),
			persist: true,
		};
		notify();
		return true;
	} catch {
		return false;
	}
}

export function subscribeScreensaverSettings(listener) {
	assert(typeof listener === 'function', 'listener must be a function');
	listeners.add(listener);
	return () => { listeners.delete(listener); };
}

/** Test / Exit helpers — reset to Flying Toasters, no persistence. */
export function resetScreensaverSettingsForTests() {
	clearPersistedSettings();
	runtimeSettings = createDefaultSettings();
	notify();
}

function assert(condition, message) {
	if (!condition) throw new Error(message);
}
